import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { MailSchema as LightMailDetailSchema } from 'modules/Mails/hooks/use-load-mail';
import { MailStatusSchema } from 'modules/Mails/types/email-status';
import { MAIL_CATEGORIES } from 'modules/Mails/types/mail-category';
import type { MailboxType } from 'modules/Mails/types/mailbox-type';
import { isDefined } from 'remeda';
import { LightDebtorSchema } from 'shared/hooks/use-load-light-debtors';
import { LightUserSchema } from 'shared/hooks/use-load-light-users';
import { addResourceNameToQueryResult } from 'shared/hooks/utils/add-resource-name-to-query-result';
import { useAxiosInstance } from 'shared/hooks/utils/axios-instance';
import { handleZodError } from 'shared/hooks/utils/handle-zod-error';
import type { ApiError } from 'shared/hooks/utils/types';
import { z } from 'zod/v4';

const MailsSearchParamsSchema = z.object({
  page: z.coerce.number().optional(),
  pageLimit: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.string().optional(),
  dateBefore: z.string().optional(),
  dateAfter: z.string().optional(),
  fromMail: z.string().optional(),
  toMail: z.string().optional(),
  subject: z.string().optional(),
  status: MailStatusSchema.optional(),
  body: z.string().optional(),
  debtorId: z.string().optional(),
  externalMailboxId: z.string().optional(),
  accountManagerId: z.string().optional(),
  read: z.boolean().optional(),
  categories: z.array(z.enum([...MAIL_CATEGORIES])).optional(),
  quickSearch: z.string().optional(),
});

const LightThreadSchema = z.object({
  id: z.coerce.string(),
  accountManager: LightUserSchema.nullable(),
  debtor: LightDebtorSchema.nullable(),
  replyCount: z.number(),
  isProcessed: z.boolean(),
  hasDraft: z.boolean(),
  mailDetail: LightMailDetailSchema.omit({
    body: true,
    cc: true,
    bcc: true,
    isDraft: true,
    attachments: true,
  }).extend({
    bodyPreview: z.string().nullable(),
  }),
});

const MailsSchema = z.object({
  threads: z.array(LightThreadSchema),
  pagination: z.object({
    totalObjects: z.number(),
    totalPages: z.number(),
    pageLimit: z.number(),
  }),
});

export type Mails = z.infer<typeof MailsSchema>;
export type LightThread = z.infer<typeof LightThreadSchema>;
export type MailsSearchParams = z.infer<typeof MailsSearchParamsSchema>;

async function loadMailsFn(axiosInstance: Promise<AxiosInstance>, { queryKey }: any) {
  const instance = await axiosInstance;
  const [, mailboxType, params] = queryKey;

  const { data } = await instance.get(`/mails/${mailboxType}`, { params });

  const safeParse = MailsSchema.safeParse(data);
  if (!safeParse.success) {
    handleZodError(safeParse, 'mails');
    throw safeParse.error;
  }

  return safeParse.data;
}

const EMPTY_MAILS: Mails = {
  threads: [],
  pagination: { totalObjects: 0, totalPages: 1, pageLimit: 20 },
};

export function useLoadMails(mailboxType: MailboxType, params?: MailsSearchParams) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<Mails, ApiError>({
    queryKey: ['mails', mailboxType, params] as const,
    queryFn: context => loadMailsFn(axiosInstance, context),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return EMPTY_MAILS;
    },
  });

  const result = addResourceNameToQueryResult<Mails, ApiError, 'mails'>('mails', queryResult);

  // placeholderData only applies while the query is pending: if the request fails before ever
  // succeeding, `mails` becomes undefined even though most consumers (e.g. the unprocessed-mail
  // navigation reading mails.threads) access it unguarded and would crash on render. The fallback
  // keeps `mails` defined in that error state — the failure itself stays observable via mailsError.
  return { ...result, mails: result.mails ?? EMPTY_MAILS };
}
